import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookCall = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [availableDates, setAvailableDates] = useState([]);
  const [loadingDates, setLoadingDates] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    companyName: '',
    currentRevenue: '',
    biggestChallenge: '',
    kickoffDate: null,
    kickoffTime: null
  });
  
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [bookingComplete, setBookingComplete] = useState(false);

  const totalSteps = 2;
  const progressPercentage = (currentStep / totalSteps) * 100;

  // Fetch available dates when reaching step 2
  useEffect(() => {
    if (currentStep === 2 && availableDates.length === 0) {
      fetchAvailableDates();
    }
  }, [currentStep]);

  const fetchAvailableDates = async () => {
    setLoadingDates(true);
    try {
      const response = await axios.get(`${API}/booking/available-dates`);
      setAvailableDates(response.data.data.available_dates);
    } catch (error) {
      console.error('Failed to fetch available dates:', error);
      // Fallback to mock dates if API fails
      setAvailableDates([
        { date: '2024-12-15', display: 'Dec 15', day_of_week: 'Sunday', available_times: ['10:00 AM', '2:00 PM'] },
        { date: '2024-12-18', display: 'Dec 18', day_of_week: 'Wednesday', available_times: ['9:00 AM', '1:00 PM'] },
        { date: '2024-12-22', display: 'Dec 22', day_of_week: 'Sunday', available_times: ['10:00 AM', '3:00 PM'] },
        { date: '2025-01-03', display: 'Jan 3', day_of_week: 'Friday', available_times: ['11:00 AM', '3:00 PM'] },
        { date: '2025-01-08', display: 'Jan 8', day_of_week: 'Wednesday', available_times: ['9:00 AM', '2:00 PM'] },
        { date: '2025-01-12', display: 'Jan 12', day_of_week: 'Sunday', available_times: ['10:00 AM', '4:00 PM'] }
      ]);
    } finally {
      setLoadingDates(false);
    }
  };

  const scheduleCall = async (dateData, timeSlot) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/booking/schedule-kickoff`, {
        customer_email: formData.email,
        customer_details: {
          first_name: formData.fullName.split(' ')[0],
          last_name: formData.fullName.split(' ').slice(1).join(' '),
          company: formData.companyName,
          phone: formData.phone,
          current_revenue: formData.currentRevenue,
          biggest_challenge: formData.biggestChallenge,
          scheduled_time: `${dateData.date}T${timeSlot}`
        }
      });
      
      console.log('Strategy call scheduled:', response.data);
      updateFormData('kickoffDate', `${dateData.display} at ${timeSlot}`);
      setBookingComplete(true);
      
      // Clear saved data
      localStorage.removeItem('callBookingData');
      
    } catch (error) {
      console.error('Failed to schedule call:', error);
      setErrors({ booking: 'Failed to schedule call. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  // Auto-save progress
  useEffect(() => {
    const saved = localStorage.getItem('callBookingData');
    if (saved) {
      setFormData(JSON.parse(saved));
    }
  }, []);

  useEffect(() => {
    if (!bookingComplete) {
      localStorage.setItem('callBookingData', JSON.stringify(formData));
    }
  }, [formData, bookingComplete]);

  const updateFormData = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 1) {
      if (!formData.fullName) newErrors.fullName = 'Full name is required';
      if (!formData.email) newErrors.email = 'Email is required';
      if (!formData.companyName) newErrors.companyName = 'Company name is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (step) => {
    if (step === 1) {
      // Capture lead info
      setLoading(true);
      try {
        await axios.post(`${API}/leads/capture`, {
          email: formData.email,
          first_name: formData.fullName.split(' ')[0],
          last_name: formData.fullName.split(' ').slice(1).join(' '),
          phone: formData.phone,
          company: formData.companyName,
          source: 'strategy-call-booking'
        });
        nextStep();
      } catch (error) {
        setErrors({ general: 'Failed to save information. Please try again.' });
      } finally {
        setLoading(false);
      }
    }
  };

  if (bookingComplete) {
    return (
      <div className="min-h-screen bg-ivory flex items-center justify-center">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto text-center">
            <Card className="editorial-card p-12">
              <div className="text-6xl mb-6">🎉</div>
              <h1 className="headline-medium text-charcoal mb-6">
                Your Strategy Call is Confirmed!
              </h1>
              <p className="body-large text-graphite mb-8">
                <strong>{formData.kickoffDate}</strong>
              </p>
              <div className="bg-teal bg-opacity-10 p-6 rounded-lg mb-8">
                <h3 className="subhead-medium text-charcoal mb-4">What to Expect:</h3>
                <ul className="space-y-2 text-left">
                  <li className="body-medium text-charcoal flex items-start">
                    <span className="text-teal mr-3">•</span>
                    45-minute deep-dive conversation about your business goals
                  </li>
                  <li className="body-medium text-charcoal flex items-start">
                    <span className="text-teal mr-3">•</span>
                    Custom growth roadmap specific to your challenges
                  </li>
                  <li className="body-medium text-charcoal flex items-start">
                    <span className="text-teal mr-3">•</span>
                    Clear next steps to scale your business
                  </li>
                  <li className="body-medium text-charcoal flex items-start">
                    <span className="text-teal mr-3">•</span>
                    Overview of how we can help accelerate your growth
                  </li>
                </ul>
              </div>
              <p className="body-medium text-graphite mb-8">
                You'll receive a calendar invite and confirmation email within the next few minutes. 
                We're excited to learn about your business and explore how we can help you scale!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="/">
                  <Button className="btn-primary">
                    Return to Homepage
                  </Button>
                </a>
                <a href="/quiz">
                  <Button variant="outline" className="btn-secondary">
                    Take the Readiness Quiz
                  </Button>
                </a>
              </div>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="headline-medium text-charcoal mb-4">Tell Us About Your Business</h2>
              <p className="body-medium text-graphite">Help us prepare for a productive conversation</p>
            </div>

            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="label-text text-charcoal block mb-2">FULL NAME *</label>
                  <input
                    type="text"
                    value={formData.fullName}
                    onChange={(e) => updateFormData('fullName', e.target.value)}
                    className={`w-full p-4 border rounded-lg focus:outline-none focus:border-bronze-strong ${
                      errors.fullName ? 'border-red-500' : 'border-bronze-light'
                    }`}
                    placeholder="John Smith"
                  />
                  {errors.fullName && <p className="text-red-500 text-sm mt-1">{errors.fullName}</p>}
                </div>

                <div>
                  <label className="label-text text-charcoal block mb-2">EMAIL *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => updateFormData('email', e.target.value)}
                    className={`w-full p-4 border rounded-lg focus:outline-none focus:border-bronze-strong ${
                      errors.email ? 'border-red-500' : 'border-bronze-light'
                    }`}
                    placeholder="john@company.com"
                  />
                  {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="label-text text-charcoal block mb-2">PHONE</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => updateFormData('phone', e.target.value)}
                    className="w-full p-4 border border-bronze-light rounded-lg focus:outline-none focus:border-bronze-strong"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>

                <div>
                  <label className="label-text text-charcoal block mb-2">COMPANY NAME *</label>
                  <input
                    type="text"
                    value={formData.companyName}
                    onChange={(e) => updateFormData('companyName', e.target.value)}
                    className={`w-full p-4 border rounded-lg focus:outline-none focus:border-bronze-strong ${
                      errors.companyName ? 'border-red-500' : 'border-bronze-light'
                    }`}
                    placeholder="Your Company Inc."
                  />
                  {errors.companyName && <p className="text-red-500 text-sm mt-1">{errors.companyName}</p>}
                </div>
              </div>

              <div>
                <label className="label-text text-charcoal block mb-2">CURRENT REVENUE STAGE</label>
                <select
                  value={formData.currentRevenue}
                  onChange={(e) => updateFormData('currentRevenue', e.target.value)}
                  className="w-full p-4 border border-bronze-light rounded-lg focus:outline-none focus:border-bronze-strong"
                >
                  <option value="">Select revenue range</option>
                  <option value="pre-revenue">Pre-revenue / Idea stage</option>
                  <option value="under-50k">&lt;$50K annually</option>
                  <option value="50k-250k">$50K–$250K annually</option>
                  <option value="over-250k">$250K+ annually</option>
                </select>
              </div>

              <div>
                <label className="label-text text-charcoal block mb-2">BIGGEST CURRENT CHALLENGE</label>
                <textarea
                  value={formData.biggestChallenge}
                  onChange={(e) => updateFormData('biggestChallenge', e.target.value)}
                  className="w-full p-4 border border-bronze-light rounded-lg focus:outline-none focus:border-bronze-strong"
                  rows="4"
                  placeholder="What's the main obstacle preventing you from scaling your business?"
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="headline-medium text-charcoal mb-4">Choose Your Call Time</h2>
              <p className="body-medium text-graphite">Book your 45-minute strategy session</p>
            </div>

            <Card className="editorial-card p-8">
              <div className="space-y-6">
                <div className="text-center">
                  <div className="text-6xl mb-4">📞</div>
                  <h3 className="subhead-medium text-charcoal">Schedule Your Strategy Call</h3>
                  <p className="body-medium text-graphite mb-6">
                    We'll dive deep into your business challenges and create a custom growth plan.
                  </p>
                </div>
                
                {loadingDates ? (
                  <div className="text-center py-8">
                    <div className="text-bronze text-lg">Loading available times...</div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {availableDates.map((dateData, index) => (
                        <Card key={index} className="p-4 hover-lift cursor-pointer border border-bronze-light">
                          <div className="text-center mb-3">
                            <div className="text-lg font-semibold text-charcoal">{dateData.display}</div>
                            <div className="text-sm text-graphite">{dateData.day_of_week}</div>
                          </div>
                          <div className="space-y-2">
                            {dateData.available_times.map((time, timeIndex) => (
                              <button
                                key={timeIndex}
                                onClick={() => {
                                  scheduleCall(dateData, time);
                                }}
                                disabled={loading}
                                className={`w-full p-2 rounded border text-sm transition-colors ${
                                  loading
                                    ? 'bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed'
                                    : 'bg-white border-bronze-light hover:border-bronze hover:bg-bronze hover:text-white'
                                }`}
                              >
                                {time}
                              </button>
                            ))}
                          </div>
                        </Card>
                      ))}
                    </div>

                    {errors.booking && (
                      <div className="bg-red-50 p-4 rounded-lg text-center">
                        <p className="text-red-600">{errors.booking}</p>
                      </div>
                    )}

                    <div className="text-center">
                      <p className="body-small text-graphite">
                        <strong>What we'll cover:</strong> Your current challenges, growth goals, and a custom roadmap to scale your business systematically.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-ivory">
      <div className="container mx-auto px-6 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Progress Bar */}
          <div className="mb-12">
            <div className="flex items-center justify-between mb-4">
              <h1 className="headline-small text-charcoal">Book Your Strategy Call</h1>
              <span className="body-small text-graphite">
                Total Time: ~2–3 min
              </span>
            </div>
            
            <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
              <div 
                className="bg-bronze h-2 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${progressPercentage}%` }}
              ></div>
            </div>
            
            <div className="flex justify-between text-xs text-graphite">
              <span className={currentStep >= 1 ? 'text-bronze font-semibold' : ''}>Your Info</span>
              <span className={currentStep >= 2 ? 'text-bronze font-semibold' : ''}>Schedule Call</span>
            </div>
          </div>

          {/* Step Content */}
          <Card className="editorial-card p-8 mb-8">
            {renderStep()}
          </Card>

          {/* Navigation */}
          {!bookingComplete && (
            <div className="flex justify-between">
              <Button
                variant="outline"
                className="btn-secondary"
                onClick={prevStep}
                disabled={currentStep === 1}
              >
                Previous
              </Button>

              <div className="text-center">
                <p className="body-small text-graphite mb-2">
                  Ready to scale your business?
                </p>
                <p className="label-text text-bronze">
                  Step {currentStep} of {totalSteps}
                </p>
              </div>

              {currentStep === 1 ? (
                <Button
                  className="btn-primary"
                  onClick={() => handleSubmit(currentStep)}
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Continue to Scheduling'}
                </Button>
              ) : (
                <div className="w-24"></div> // Spacer for alignment
              )}
            </div>
          )}

          {errors.general && (
            <p className="text-red-500 text-center mt-4">{errors.general}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookCall;